import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "../components/ConfirmDialog";
import StaffModal from "../components/StaffModal";
import Toast from "../components/Toast";
import useUsers from "../hooks/useUsers";
import { useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { deleteUser } from "../services/userService";
import { fetchRoles } from "../services/roleService";

/**
 * Every branch a user is assigned to — the ones they run plus the ones their
 * login may reach. Since OUP-41 a receptionist or a staff member is assigned
 * branches without managing any, so a column that listed only managed branches
 * read as "no branches" for everybody but a manager.
 */
const assignedBranches = (user) => {
  const byId = new Map();

  [...(user.managed_branches ?? []), ...(user.branches ?? [])].forEach((branch) =>
    byId.set(branch.id, branch)
  );

  return [...byId.values()];
};

export default function Staff() {
  const { t, i18n } = useTranslation();

  const {
    users,
    meta,
    page,
    setPage,
    filters,
    applyFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    submitSearch,
    loading,
    error,
    reload,
  } = useUsers();

  // Back from a detail page lands where the user was.
  useScrollRestoration(!loading);

  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  // A stale id in the URL (deleted, or not this user's) falls back to "any".
  useValidOption(filters.roleId, roles.length ? roles : null, () => applyFilters({ roleId: "" }));

  // The branch picker needs every branch, plus who currently runs each one.
  const loadBranches = useCallback(async () => {
    try {
      const { branches: list } = await fetchBranches(1, 100);
      setBranches(list);
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches, i18n.language]);

  // The role picker and the role filter both read the business's own roles.
  useEffect(() => {
    fetchRoles()
      .then(setRoles)
      .catch(() => setRoles([]));
  }, []);

  const filtersApplied = filters.search || filters.roleId || filters.isActive !== "";

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteUser(deleting.id);
      setToast({ type: "success", message: t("staff.deleted", { name: deleting.name }) });
      setDeleting(null);
      reload();
      loadBranches();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("staff.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("staff.title")}</h1>
          <p className="text-sm text-ink-2">
            {meta ? t("staff.count", { count: meta.total }) : t("staff.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="btn-primary"
        >
          {t("staff.new")}
        </button>
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="card mb-4 flex flex-wrap items-end gap-3 p-4"
      >
        <div className="min-w-[200px] flex-1">
          <label className="field-label" htmlFor="staff-search">
            {t("staff.filters.search")}
          </label>
          <input
            id="staff-search"
            className="field-input"
            placeholder={t("staff.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="staff-role-filter">
            {t("staff.filters.role")}
          </label>
          <select
            id="staff-role-filter"
            className="field-input"
            value={filters.roleId}
            onChange={(e) => applyFilters({ roleId: e.target.value })}
          >
            <option value="">{t("staff.filters.any")}</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.slug ? t(`roles.system.${role.slug}`, role.name) : role.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="staff-active-filter">
            {t("staff.filters.status")}
          </label>
          <select
            id="staff-active-filter"
            className="field-input"
            value={filters.isActive}
            onChange={(e) => applyFilters({ isActive: e.target.value })}
          >
            <option value="">{t("staff.filters.any")}</option>
            <option value="1">{t("staff.active")}</option>
            <option value="0">{t("staff.inactive")}</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button type="submit" className="btn-primary">
            {t("staff.filters.apply")}
          </button>
          {filtersApplied && (
            <button
              type="button"
              onClick={() => {
                resetFilters();
              }}
              className="btn-secondary"
            >
              {t("staff.filters.clear")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : users.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {filtersApplied ? t("staff.noMatches") : t("staff.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("staff.table.name")}</th>
                  <th className="px-4 py-3">{t("staff.table.role")}</th>
                  <th className="px-4 py-3">{t("staff.table.branches")}</th>
                  <th className="px-4 py-3">{t("staff.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-ink-2" dir="ltr">
                        {user.email}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          user.is_owner
                            ? "bg-raised text-ink-2"
                            : "bg-accent-wash text-accent-text"
                        }`}
                      >
                        {user.role_slug
                          ? t(`roles.system.${user.role_slug}`, user.role)
                          : user.role ?? t("common.none")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BranchPills
                        branches={assignedBranches(user)}
                        empty={t("staff.noBranches")}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {user.is_active ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("staff.active")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text">
                          {t("staff.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(user);
                            setModalOpen(true);
                          }}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </button>
                        {/* The owner can never be removed. */}
                        {!user.is_owner && (
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteError("");
                              setDeleting(user);
                            }}
                            className="text-sm font-medium text-danger-text hover:underline"
                          >
                            {t("common.delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <StaffModal
        user={editing}
        branches={branches}
        roles={roles}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setToast({
            type: "success",
            message: wasEditing
              ? t("staff.updated", { name: saved.name })
              : t("staff.created", { name: saved.name }),
          });
          reload();
          // Assignments may have moved branches between managers.
          loadBranches();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("staff.deleteTitle")}
        message={t("staff.deleteMessage", { name: deleting?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/** The branches on one staff row, or a note when they are assigned none. */
function BranchPills({ branches, empty }) {
  if (branches.length === 0) {
    return <span className="text-xs text-ink-2">{empty}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {branches.map((branch) => (
        <span key={branch.id} className="rounded-full bg-raised px-2 py-1 text-xs text-ink">
          {branch.name}
        </span>
      ))}
    </div>
  );
}
